import { z } from 'zod';
import {
  idParamSchema,
  moneySchema,
  optionalString,
  paginationQuerySchema,
  requiredString,
} from './common';
import { type ExpenseKind, expenseKindSchema } from './enums';

/**
 * Money out, and the reports built on it.
 *
 * The one rule worth stating up front: **reagent cost is never entered here.**
 * It is derived from the stock ledger — `SUM(quantity_base × unit_cost)` over
 * consumption movements — so a category for it would let somebody enter the same
 * spend twice and quietly halve the margin.
 */

export const createExpenseSchema = z.object({
  category_id: idParamSchema,
  /** The day it was incurred, not the day it was keyed in. */
  incurred_on: z.iso.date(),
  amount: moneySchema,
  description: requiredString('Description'),
  vendor: optionalString(),
  reference: optionalString(),
  note: optionalString(),
});
export type CreateExpenseInput = z.infer<typeof createExpenseSchema>;
export const updateExpenseSchema = createExpenseSchema;
export type UpdateExpenseInput = z.infer<typeof updateExpenseSchema>;

export const expenseQuerySchema = paginationQuerySchema.extend({
  category_id: idParamSchema.optional(),
  kind: expenseKindSchema.optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});
export type ExpenseQuery = z.infer<typeof expenseQuerySchema>;

export const createExpenseCategorySchema = z.object({
  name: requiredString('Name'),
  code: requiredString('Code'),
  kind: expenseKindSchema,
  is_active: z.boolean().default(true),
});
export type CreateExpenseCategoryInput = z.infer<typeof createExpenseCategorySchema>;

/* ------------------------------------------------------------------------- *
 * Wire shapes
 * ------------------------------------------------------------------------- */

export interface ExpenseCategoryRow {
  id: number;
  name: string;
  code: string;
  kind: ExpenseKind;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExpenseRow {
  id: number;
  /** ISO date-time, but only the calendar day is meaningful. */
  incurred_on: string;
  amount: number;
  description: string;
  vendor: string | null;
  reference: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
  category: { id: number; name: string; code: string; kind: ExpenseKind };
  user: { id: number; first_name: string; last_name: string | null };
}

/* ------------------------------------------------------------------------- *
 * Reports
 * ------------------------------------------------------------------------- */

/** Inclusive calendar dates, in the lab's own timezone. */
export const dateRangeQuerySchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
});
export type DateRangeQuery = z.infer<typeof dateRangeQuerySchema>;

/**
 * How the income breakdown is grouped.
 *
 * `day` for a trend, the rest for a ranking. `panel` and `user` exist because
 * "which referrer earns most" and "who registered this" are the two questions a
 * lab owner asks that a per-test breakdown cannot answer.
 */
export const incomeGroupSchema = z.enum(['day', 'test', 'department', 'panel', 'user']);
export type IncomeGroup = z.infer<typeof incomeGroupSchema>;

export const INCOME_GROUP_LABELS = {
  day: 'By day',
  test: 'By test',
  department: 'By bench',
  panel: 'By panel',
  user: 'By who registered',
} as const satisfies Record<IncomeGroup, string>;

export const incomeQuerySchema = dateRangeQuerySchema.extend({
  by: incomeGroupSchema.default('day'),
});
export type IncomeQuery = z.infer<typeof incomeQuerySchema>;

export interface IncomeRow {
  key: string;
  label: string;
  /** Visits, or reports when grouped by test — the label says which. */
  count: number;
  billed: number;
  collected: number;
  outstanding: number;
}

export interface IncomeTotals {
  count: number;
  billed: number;
  collected: number;
  outstanding: number;
}

export interface IncomeReport {
  from: string;
  to: string;
  by: IncomeGroup;
  /** What `count` counts, in words, because it changes with the grouping. */
  count_label: string;
  /**
   * Whether `collected` and `outstanding` mean anything at this grouping.
   *
   * False for the per-test and per-bench breakdowns, where payment cannot be
   * attributed to a line item. The client hides those columns rather than
   * printing a zero, which would read as "nothing was paid" instead of "this
   * cannot be known".
   */
  collected_known: boolean;
  rows: IncomeRow[];
  totals: IncomeTotals;
}

/* ------------------------------------------------------------------------- *
 * Profit and loss
 * ------------------------------------------------------------------------- */

export interface ProfitLossLine {
  kind: string;
  label: string;
  amount: number;
  /** Worked out by the app rather than typed in — the reagent line. */
  derived: boolean;
}

export interface ProfitLoss {
  from: string;
  to: string;
  /** `Patient.total` over the period — what was billed, discounts applied. */
  billed: number;
  collected: number;
  /** Reagent consumed at cost, from the stock ledger. Never entered by hand. */
  reagent_cost: number;
  expenses: ProfitLossLine[];
  total_expenses: number;
  /** Billed less every cost above. */
  surplus: number;
  /**
   * Cost categories with nothing recorded in this period.
   *
   * Rendered as a strip on the screen, not hidden. A profit-and-loss that
   * quietly omits salaries is worse than none: the first month shows an absurd
   * margin, and after that nobody trusts the screen again.
   */
  missing: { kind: string; label: string }[];
}

/* ------------------------------------------------------------------------- *
 * Year-wise history
 * ------------------------------------------------------------------------- */

export interface HistoryYear {
  year: number;
  patients: number;
  reports: number;
  /** Null for a role without billing access — withheld server-side. */
  billed: number | null;
  collected: number | null;
  /** Visits whose date was reconstructed from a year alone, on import. */
  approximate: number;
}
